/**
 * ScaffoldPreviewStep — ③단계 미리보기·생성 (queue #642 · docs/biz-specs/project-scaffold.md §6.2/§6.4).
 *
 * 진입 시 `POST /api/eraf/scaffold/previews` 를 1회 호출해 pom.xml/application.yml/파일트리를
 * 렌더한다(#639/#637 SSOT 는 BE `ScaffoldCatalogAdapter` 가 소비 — FE 는 재계산하지 않는다).
 * [프로젝트 골격 생성] 클릭 → FormDialog 확인(전이 자동포함 고지) → 확인 시
 * `POST /api/eraf/scaffold/projects` → blob 다운로드(#631/#641 패턴 재사용).
 *
 * queue #682 §UAT-2026-07-14-R682 §R682-5⑤: 생성 확인 다이얼로그에 "생성 후 카트 비우기"
 * 체크(기본 on)를 추가한다. zip 생성 성공 후 체크 상태면 `cart.clear()` — 실패 시에는
 * 재시도 대비로 카트를 비우지 않는다.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog, ErrorState } from '@ui-kit';
import { useScaffoldPreview } from '../hooks/useScaffoldPreview';
import { useScaffoldGenerate } from '../hooks/useScaffoldGenerate';
import { recordCatalogSignals } from '../api/catalogSignalApi';
import { useScaffoldCart } from '../../_shared/hooks/useScaffoldCart';
import type { ScaffoldMetaFormValues } from '../schema/scaffold';
import type { ApiError } from '../../common-showcase/types/showcase';

interface Props {
  meta: ScaffoldMetaFormValues;
  moduleCodes: string[];
  onBack: () => void;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function todayBasicIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
}

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: 'var(--color-surface-elevated)',
};
const cardHeadStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-2)',
  padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-surface-muted)', borderBottom: '1px solid var(--color-surface-border)',
};
const cardBodyStyle: React.CSSProperties = { padding: 'var(--spacing-3)', overflowX: 'auto', maxHeight: 300, overflowY: 'auto' };
const preStyle: React.CSSProperties = { fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', lineHeight: 1.6, whiteSpace: 'pre', margin: 0 };
const chipStyle: React.CSSProperties = {
  display: 'inline-block', fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)', padding: '1px var(--spacing-2)',
  borderRadius: 'var(--radius-full)', border: '1px solid var(--color-surface-border)', background: 'var(--color-surface-background)', color: 'var(--color-text-secondary)',
};
const chipTransitiveStyle: React.CSSProperties = { ...chipStyle, borderStyle: 'dashed', borderColor: 'var(--color-info)', color: 'var(--color-info)' };
// §R681-3(b) — web/ 접두 파일을 BE 파일과 시각 구분하는 선택적 배지.
const webBadgeStyle: React.CSSProperties = {
  fontSize: 10, marginLeft: 'var(--spacing-2)', padding: '0 var(--spacing-2)', borderRadius: 'var(--radius-full)',
  border: '1px solid var(--color-brand-primary)', color: 'var(--color-brand-primary)',
};
const btnGhost: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600,
  cursor: 'pointer', border: '1px solid var(--color-surface-border)', background: 'transparent', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-sans)',
};
const btnPrimary: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 700,
  cursor: 'pointer', border: 'none', background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', fontFamily: 'var(--font-family-sans)',
  display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)',
};

const SAMPLE_CONTROLLER_SNIPPET = `// ★ 빈이름 충돌 예방(#559)
@RestController("helloController")
@RequestMapping("/hello")
class HelloController {
  @GetMapping
  ApiResponse<String> hello() {
    return ApiResponse.ok("hi"); // C002
  }
}`;

export function ScaffoldPreviewStep({ meta, moduleCodes, onBack }: Props) {
  const { t } = useTranslation();
  const preview = useScaffoldPreview();
  const generate = useScaffoldGenerate();
  const cart = useScaffoldCart();
  const [dialogOpen, setDialogOpen] = useState(false);
  // §R682-5⑤: "생성 후 카트 비우기" — 기본 on.
  const [clearCartOnGenerate, setClearCartOnGenerate] = useState(true);
  const requested = useRef(false);

  const request = { ...meta, moduleCodes };

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    preview.mutate(request);
    // 최초 진입 시 1회만 호출 — meta/moduleCodes 는 단계 진입 시점에 고정된 props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleGenerateClick() {
    setDialogOpen(true);
  }

  function handleConfirmGenerate() {
    setDialogOpen(false);
    generate.reset();
    generate.mutate(request, {
      onSuccess: ({ blob, filename }) => {
        downloadBlob(blob, filename);
        // queue #678 §R678-4④: 선택 모듈별 TAKE 시그널(fire-and-forget) — SCAFFOLD 다운로드
        // 행은 ScaffoldController 가 별도 계상(무변경).
        void recordCatalogSignals('TAKE', moduleCodes).catch(() => {});
        // §R682-5⑤: 생성 성공 + 체크 상태면 카트 비우기(실패 시에는 비우지 않음 — 재시도 대비).
        if (clearCartOnGenerate) cart.clear();
      },
    });
  }

  const previewError = preview.error as ApiError | null;
  const generateError = generate.error as ApiError | null;
  const generateGuidance =
    generateError?.statusCode === 429
      ? t('projectScaffold.error.429', { seconds: 60 })
      : generateError?.statusCode === 400
      ? t('projectScaffold.error.400')
      : null;

  const data = preview.data;
  const expectedFilename = `${meta.projectName}-scaffold-${todayBasicIso()}.zip`;

  return (
    <div style={{ background: 'var(--color-surface-muted)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-6)' }}>
      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-1)' }}>
        {`③ ${t('projectScaffold.step.preview')}`}
      </p>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-4)' }}>
        {t('projectScaffold.step.previewDesc')}
      </p>

      {preview.isPending && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>{t('projectScaffold.preview.loading')}</p>
      )}

      {preview.isError && (
        <ErrorState title={t('projectScaffold.preview.errorTitle')} message={previewError?.message} onRetry={() => preview.mutate(request)} />
      )}

      {preview.isSuccess && data && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', minWidth: 96, textTransform: 'uppercase' }}>
                {t('projectScaffold.preview.includedArtifacts')}
              </span>
              <span>{data.includedArtifacts.map((a) => <span key={a} style={{ ...chipStyle, marginRight: 4 }}>{a}</span>)}</span>
            </div>
            {data.transitiveCodes.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', minWidth: 96, textTransform: 'uppercase' }}>
                  {t('projectScaffold.preview.transitiveLabel')}
                </span>
                <span>{data.transitiveCodes.map((c) => <span key={c} style={{ ...chipTransitiveStyle, marginRight: 4 }}>{c}</span>)}</span>
              </div>
            )}
            {data.optInGates.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', minWidth: 96, textTransform: 'uppercase' }}>
                  {t('projectScaffold.preview.optInLabel')}
                </span>
                <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-brand-primary)' }}>
                  {data.optInGates.join(', ')}
                </code>
              </div>
            )}
          </div>

          {data.hardInjectWarnings.length > 0 && (
            <div
              role="alert"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)', padding: 'var(--spacing-3) var(--spacing-4)', marginBottom: 'var(--spacing-4)',
                background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))', border: '1px solid var(--color-warning)',
                borderLeft: '4px solid var(--color-warning)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)',
              }}
            >
              <span aria-hidden="true" style={{ color: 'var(--color-warning)', fontSize: 'var(--font-size-lg)' }}>⚠</span>
              <span>{t('projectScaffold.hardInjectWarning')}: {data.hardInjectWarnings.join(', ')}</span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4)' }}>
            <div style={cardStyle}>
              <div style={cardHeadStyle}>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)' }}>
                  {t('projectScaffold.preview.pomTitle')}
                </span>
              </div>
              <div style={cardBodyStyle}><pre style={preStyle}>{data.pomXml}</pre></div>
            </div>

            <div style={cardStyle}>
              <div style={cardHeadStyle}>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)' }}>
                  {t('projectScaffold.preview.ymlTitle')}
                </span>
              </div>
              <div style={cardBodyStyle}><pre style={preStyle}>{data.applicationYml}</pre></div>
            </div>

            <div style={cardStyle}>
              <div style={cardHeadStyle}>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)' }}>
                  {t('projectScaffold.preview.filesTitle')}
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, padding: '1px var(--spacing-2)', borderRadius: 'var(--radius-full)', border: '1px solid var(--color-surface-border)', color: 'var(--color-text-muted)' }}>
                  {t('projectScaffold.preview.fileCount', { count: data.files.length })}
                </span>
              </div>
              <div style={cardBodyStyle}>
                <div role="list" style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', lineHeight: 1.9, color: 'var(--color-text-secondary)' }}>
                  {data.files.map((f) => (
                    <div role="listitem" key={f.path}>
                      📄 <span style={{ color: 'var(--color-text-primary)' }}>{f.path}</span>
                      <span style={{ fontSize: 10, marginLeft: 'var(--spacing-2)', padding: '0 var(--spacing-2)', borderRadius: 'var(--radius-full)', border: '1px solid var(--color-surface-border)', color: 'var(--color-text-muted)' }}>
                        {f.kind}
                      </span>
                      {f.path.startsWith('web/') && (
                        <span style={webBadgeStyle}>{t('projectScaffold.preview.webBadge')}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 샘플 소스는 API 응답에 본문이 포함되지 않는 정적 예시(경로/종류만 files 에 포함) */}
            <div style={cardStyle}>
              <div style={cardHeadStyle}>
                <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-mono)' }}>
                  {t('projectScaffold.preview.sampleTitle')}
                </span>
              </div>
              <div style={cardBodyStyle}><pre style={preStyle}>{SAMPLE_CONTROLLER_SNIPPET}</pre></div>
            </div>
          </div>
        </>
      )}

      {generate.isError && generateError && (
        <div style={{ marginTop: 'var(--spacing-4)' }}>
          <ErrorState
            title={t('projectScaffold.generateErrorTitle')}
            message={generateGuidance ? `${generateError.message} ${generateGuidance}` : generateError.message}
            onRetry={handleGenerateClick}
          />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-6)', flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} disabled={generate.isPending} style={{ ...btnGhost, ...(generate.isPending ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}>
          {t('projectScaffold.step.back')}
        </button>
        <button
          type="button"
          onClick={handleGenerateClick}
          disabled={!data || generate.isPending}
          aria-busy={generate.isPending}
          style={{ ...btnPrimary, ...(!data || generate.isPending ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
        >
          {generate.isPending && (
            <span aria-hidden="true" style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: 'var(--color-text-inverse)', borderRadius: 'var(--radius-full)', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
          )}
          {generate.isPending ? t('projectScaffold.generating') : `⬇ ${t('projectScaffold.generate')}`}
        </button>
      </div>

      <FormDialog
        open={dialogOpen}
        title={t('projectScaffold.confirm.title', { count: moduleCodes.length + (data?.transitiveCodes.length ?? 0) })}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleConfirmGenerate}
        submitLabel={`⬇ ${t('projectScaffold.generate')}`}
        cancelLabel={t('projectScaffold.confirm.cancel')}
        submitTestId="scaffold-confirm-generate"
      >
        <p>{t('projectScaffold.confirm.body', { filename: expectedFilename })}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', minWidth: 96, textTransform: 'uppercase' }}>
              {t('projectScaffold.confirm.selectedLabel')}
            </span>
            <span>{moduleCodes.map((c) => <span key={c} style={{ ...chipStyle, marginRight: 4 }}>{c}</span>)}</span>
          </div>
          {data && data.transitiveCodes.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: 'var(--color-text-muted)', minWidth: 96, textTransform: 'uppercase' }}>
                {t('projectScaffold.confirm.transitiveLabel')}
              </span>
              <span>{data.transitiveCodes.map((c) => <span key={c} style={{ ...chipTransitiveStyle, marginRight: 4 }}>{c}</span>)}</span>
            </div>
          )}
        </div>
        <div
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)', padding: 'var(--spacing-3) var(--spacing-4)', marginTop: 'var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-info) 10%, var(--color-surface-muted))', border: '1px solid var(--color-info)', borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)',
          }}
        >
          <span aria-hidden="true">💡</span>
          <span>{t('projectScaffold.confirm.hint')}</span>
        </div>
        {/* queue #682 §R682-5⑤: 생성 후 카트 비우기(기본 on) */}
        <label
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)',
            fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={clearCartOnGenerate}
            onChange={(e) => setClearCartOnGenerate(e.target.checked)}
            style={{ accentColor: 'var(--color-brand-primary)', width: 15, height: 15 }}
          />
          {t('showcase.cart.clearAfterGenerate')}
        </label>
      </FormDialog>
    </div>
  );
}
